import React, { useState, useEffect, useCallback } from 'react';
import { getStorage } from 'firebase/storage';
import { 
  collection, query, where, onSnapshot, orderBy, limit, 
  doc, getDoc, getDocs, getCountFromServer 
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import {
  LogOut, Home, Users, BookUser, GraduationCap, ClipboardList, Wallet, Megaphone,
  Settings, UserPlus, FileText, CalendarDays, Key, LayoutDashboard, List, CreditCard,
  DollarSign, Bell, UserRoundCheck, ClipboardCheck, BookOpenText, School, 
  UserCog, Loader2, XCircle, Link, Menu, X, QrCode, Smartphone,
  FileLock, ClipboardList as ListCheck
} from 'lucide-react';

// --- IMPORT DES COMPOSANTS ---
import ElevesManagement from './ElevesManagement';
import EnseignantsManagement from './EnseignantsManagement';
import MatieresManagement from './MatieresManagement';
import EmploiDuTempsManagement from './EmploiDuTempsManagement';
import NotesManagement from './NotesManagement';
import PaiementsManagement from './PaiementsManagement';
import AnnoncesManagement from './AnnoncesManagement';
import UserRegistrationForm from './UserRegistrationForm';
import UserTable from './UserTable';
import SubscriptionManagement from './SubscriptionManagement';
import BulletinManagement from './BulletinManagement';
import CoursManagement from './CoursManagement';
import UserRoleManagement from './UserRoleManagement';
import SuperAdminSchoolManagement from './SuperAdminSchoolManagement';
import ClassesFraisManagement from './ClassesFraisManagement';
import StudentExcelImport from './StudentExcelImport';
import DepensesManagement from './DepensesManagement';
import BankingManagement from './BankingManagement';
import FinancialStatementsManagement from './FinancialStatementsManagement';
import SchoolInfoManagement from './SchoolInfoManagement';
import MyPaiements from './MyPaiements';
import MyNotes from './MyNotes';
import MyBulletins from './MyBulletins';
import AIChatAssistant from './AIChatAssistant';
import UserProfileForm from './UserProfileForm'; 
import RetardsAbsencesManagement from './RetardsAbsencesManagement';
import TeacherAttendanceInput from './TeacherAttendanceInput';
import MyRetardsAbsences from './MyRetardsAbsences';
import ParentChildLinking from './ParentChildLinking';
import MyEmploiDuTemps from './MyEmploiDuTemps';
import RapportCaisse from './RapportCaisse';
import ConsumablesManagement from './ConsumablesManagement';
import TeacherAttendanceReport from './TeacherAttendanceReport';
import WaveQRPaymentSimple from './WaveQRPaymentSimple';
import WaveQRUpload from './WaveQRUpload';

// Import du Context
import { useUser } from './UserContext';
import { db } from '../firebase';

// --- CONFIGURATION DES VERSIONS ---
const VERSION_FEATURES = {
  v1: [
    'dashboard', 'elevesManagement', 'enseignantsManagement', 
    'matieresManagement', 'emploiDuTempsManagement', 'notesManagement', 
    'bulletinManagement', 'coursManagement', 'annoncesManagement', 
    'schoolInfoManagement', 'classesFraisManagement', 'paiementsManagement',
    'UserProfileForm', 'userRegistration', 'userRoleManagement', 'userList',
    'RetardsAbsencesManagement', 'studentExcelImport', 'parentChildLinking',
    'depensesManagement', 'waveQRPayment'
  ],
  v2: [
    'Rapport', 
    'ConsumablesManagement',
    'teacherAttendance',
  ],
  v3: [
    'bankingManagement', 
    'financialStatementsManagement', 
    'waveQRConfig'
  ]
};

const AdminDashboard = ({ setMessage, setError, storage }) => {
  const { userAuth, userProfile, isLoadingUser } = useUser();
  const userId = userAuth?.uid;
  const userEmail = userAuth?.email;
  const userRole = userProfile?.role;
  const schoolId = userProfile?.schoolId;
  const schoolName = userProfile?.schoolName;
  const schoolLogoUrl = userProfile?.schoolLogoUrl;

  // États UI
  const [selectedSection, setSelectedSection] = useState('dashboard');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // États Données
  const [totalStudents, setTotalStudents] = useState(0);
  const [totalTeachers, setTotalTeachers] = useState(0);
  const [totalPayments, setTotalPayments] = useState(0);
  const [recentAnnouncements, setRecentAnnouncements] = useState([]);
  const [totalRegisteredUsers, setTotalRegisteredUsers] = useState(0);
  const [loadingStats, setLoadingStats] = useState(false); 

  const [selectedSchoolForSuperAdmin, setSelectedSchoolForSuperAdmin] = useState(null);
  const [allSchoolsList, setAllSchoolsList] = useState([]); 
  const [appVersion, setAppVersion] = useState('v1');
  const [waveQrCodeUrl, setWaveQrCodeUrl] = useState(null);

  // Déterminer l'ID de l'école active
  const effectiveSchoolId = userRole === 'superAdmin' ? selectedSchoolForSuperAdmin : schoolId;

  // --- CHARGER LE QR CODE WAVE (pour toutes les écoles) ---
  useEffect(() => {
    const fetchWaveQrCode = async () => {
      try {
        const qrConfigRef = doc(db, 'system_config', 'wave_qr_code');
        const qrConfigSnap = await getDoc(qrConfigRef);
        
        if (qrConfigSnap.exists()) {
          const configData = qrConfigSnap.data();
          setWaveQrCodeUrl(configData.qrCodeUrl);
        }
      } catch (error) {
        console.error("Erreur chargement QR Wave:", error);
        setError("Erreur lors du chargement du QR Code Wave.");
      }
    };

    fetchWaveQrCode();
  }, [db, setError]);

  // --- NAVIGATION HELPER ---
  const handleNavigation = (section) => {
    setSelectedSection(section);
    setIsMobileMenuOpen(false);
  };

  // --- VERSION CHECK ---
  useEffect(() => {
    if (!effectiveSchoolId) return;

    const fetchAppVersion = async () => {
      try {
        const schoolDocRef = doc(db, 'schools', effectiveSchoolId);
        const schoolSnap = await getDoc(schoolDocRef);
        
        if (schoolSnap.exists()) {
          const data = schoolSnap.data();
          setAppVersion(data.appVersion || 'v1');
        } else {
          setAppVersion('v1');
        }
      } catch (err) {
        console.error("Erreur chargement version école:", err);
        setAppVersion('v1');
      }
    };

    fetchAppVersion();
  }, [effectiveSchoolId]);

  const hasAccess = (sectionName) => {
    if (VERSION_FEATURES.v1.includes(sectionName)) return true;
    if (appVersion === 'v2' && VERSION_FEATURES.v2.includes(sectionName)) return true;
    if (appVersion === 'v3' && VERSION_FEATURES.v3.includes(sectionName)) return true;
    return false;
  };

  const handleLogout = useCallback(() => {
    getAuth().signOut();
  }, []);

  // SuperAdmin Logic
  useEffect(() => {
    if (userProfile && userProfile.schoolId && userProfile.role !== 'superAdmin') {
      setSelectedSchoolForSuperAdmin(userProfile.schoolId);
    }
  }, [userProfile]);

  useEffect(() => {
    if (!userRole) return; 
    setError('');
    setMessage('');

    if (userRole === 'superAdmin') {
      const schoolsCollectionRef = collection(db, 'schools');
      const unsubscribeAllSchools = onSnapshot(schoolsCollectionRef, (snapshot) => {
        const schoolsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAllSchoolsList(schoolsData);

        if (userProfile?.schoolId && !selectedSchoolForSuperAdmin) {
          if (schoolsData.some(school => school.id === userProfile.schoolId)) {
            setSelectedSchoolForSuperAdmin(userProfile.schoolId);
          }
        }
      }, (error) => {
        console.error("Error fetching all schools for SuperAdmin:", error);
        setError("Erreur lors du chargement de la liste des écoles.");
      });
      return () => unsubscribeAllSchools();
    }
  }, [db, userRole, userProfile, selectedSchoolForSuperAdmin, setMessage, setError]); 

  // Stats Logic
  useEffect(() => {
    if (!userProfile) return; 
    const currentDashboardSchoolId = effectiveSchoolId; 

    const fetchDashboardStats = async () => {
      if (!currentDashboardSchoolId && userRole !== 'superAdmin') { 
        setTotalStudents(0); 
        setTotalTeachers(0); 
        setTotalPayments(0);
        setRecentAnnouncements([]); 
        setTotalRegisteredUsers(0);
        return;
      }
      setLoadingStats(true);
      try {
        let usersQuery;
        if (userRole === 'superAdmin' && currentDashboardSchoolId) { 
          usersQuery = query(collection(db, 'users'), where('schoolId', '==', currentDashboardSchoolId));
        } else if (userRole === 'admin' && schoolId) { 
          usersQuery = query(collection(db, 'users'), where('schoolId', '==', schoolId));
        }

        if (usersQuery) {
          const snapshot = await getCountFromServer(usersQuery);
          setTotalRegisteredUsers(snapshot.data().count);
        } else {
          setTotalRegisteredUsers(0);
        }

        if (currentDashboardSchoolId) {
          const studentsRef = collection(db, 'schools', currentDashboardSchoolId, 'eleves');
          const studentsSnap = await getCountFromServer(studentsRef);
          setTotalStudents(studentsSnap.data().count);

          const teachersRef = collection(db, 'schools', currentDashboardSchoolId, 'enseignants');
          const teachersSnap = await getCountFromServer(teachersRef);
          setTotalTeachers(teachersSnap.data().count);

          const paymentsRef = collection(db, 'schools', currentDashboardSchoolId, 'paiements');
          const paymentsSnap = await getDocs(paymentsRef);
          let sum = 0;
          paymentsSnap.docs.forEach(doc => { sum += doc.data().amount || 0; });
          setTotalPayments(sum);

          const announcementsQuery = query(
            collection(db, 'schools', currentDashboardSchoolId, 'announcements'), 
            orderBy('createdAt', 'desc'), 
            limit(3)
          );
          const announcementsSnap = await getDocs(announcementsQuery);
          const announcementsList = announcementsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setRecentAnnouncements(announcementsList);
        }
      } catch (error) {
        console.error("Erreur chargement stats:", error);
        setError("Erreur partielle lors du chargement des statistiques.");
      } finally {
        setLoadingStats(false);
      }
    };
    fetchDashboardStats();
  }, [db, userProfile, userRole, schoolId, effectiveSchoolId, setError]); 

  const handleSchoolSelectionChange = (e) => {
    const newSchoolId = e.target.value;
    setSelectedSchoolForSuperAdmin(newSchoolId === "null" ? null : newSchoolId);
    setSelectedSection('dashboard');
  };

  // Fonction pour mettre à jour l'URL du QR code après upload
  const handleQrUploadComplete = (url) => {
    setWaveQrCodeUrl(url);
    setMessage('QR code Wave configuré avec succès !');
  };

  // --- CONTENU PRINCIPAL ---
  const renderContent = () => {
    if (userRole === 'superAdmin' && !effectiveSchoolId &&
      !['superAdminSchoolManagement', 'userRegistration', 'userList', 'userRoleManagement', 'dashboard', 'UserProfileForm', 'waveQRPayment', 'waveQRConfig'].includes(selectedSection)) { 
      return (
        <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800">
          <p className="font-semibold text-lg mb-2">Sélection requise.</p>
          <p>Veuillez sélectionner une école pour accéder à ces fonctionnalités.</p>
        </div>
      );
    }

    switch (selectedSection) {
      case 'dashboard':
        return (
          <div className="p-4 md:p-6">
            <h3 className="text-xl md:text-2xl font-semibold text-gray-700 mb-6">Aperçu du Tableau de Bord</h3>
            {userRole === 'superAdmin' && selectedSchoolForSuperAdmin && (
              <p className="text-gray-600 mb-4 text-sm md:text-base">
                Visualisation : <span className="font-bold">{allSchoolsList.find(s => s.id === selectedSchoolForSuperAdmin)?.name || selectedSchoolForSuperAdmin}</span>
                <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">Version {appVersion}</span>
              </p>
            )}

            {(userRole === 'superAdmin' || userRole === 'admin' || userRole === 'financier') && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg shadow p-4 md:p-6 flex items-center justify-between">
                  <div> <h4 className="text-xs md:text-sm font-light opacity-80">Total Élèves</h4> <p className="text-2xl md:text-4xl font-bold mt-1">{totalStudents}</p> </div>
                  <GraduationCap size={40} className="opacity-70" />
                </div>
                <div className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg shadow p-4 md:p-6 flex items-center justify-between">
                  <div> <h4 className="text-xs md:text-sm font-light opacity-80">Total Professeurs</h4> <p className="text-2xl md:text-4xl font-bold mt-1">{totalTeachers}</p> </div>
                  <BookUser size={40} className="opacity-70" />
                </div>
                <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg shadow p-4 md:p-6 flex items-center justify-between">
                  <div> <h4 className="text-xs md:text-sm font-light opacity-80">Utilisateurs</h4> <p className="text-2xl md:text-4xl font-bold mt-1">{totalRegisteredUsers}</p> </div>
                  <Users size={40} className="opacity-70" />
                </div>
                <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white rounded-lg shadow p-4 md:p-6 flex items-center justify-between">
                  <div> <h4 className="text-xs md:text-sm font-light opacity-80">Total Paiements</h4> <p className="text-xl md:text-3xl font-bold mt-1 truncate">{totalPayments.toLocaleString('fr-FR')} F</p> </div>
                  <DollarSign size={40} className="opacity-70" />
                </div>
              </div>
            )}

            <div className="bg-white rounded-lg shadow p-4 md:p-6 border border-gray-200">
              <h4 className="text-lg md:text-xl font-semibold text-gray-800 mb-4 flex items-center">
                <Bell size={24} className="mr-3 text-red-500" /> Annonces Récentes
              </h4>
              {recentAnnouncements.length > 0 ? (
                <ul className="space-y-3">
                  {recentAnnouncements.map((annonce) => (
                    <li key={annonce.id} className="p-3 md:p-4 bg-gray-50 rounded-md border border-gray-100 flex flex-col md:flex-row md:items-start">
                      <span className="text-xs md:text-sm font-medium text-gray-600 mr-3 mb-1 md:mb-0 md:mt-1.5">
                        {new Date(annonce.createdAt?.toDate ? annonce.createdAt.toDate() : annonce.createdAt).toLocaleDateString()}
                      </span>
                      <div>
                        <p className="font-semibold text-gray-900 text-sm md:text-base">{annonce.title}</p>
                        <p className="text-sm text-gray-700 mt-1">{annonce.content}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-600 italic text-sm">Aucune annonce récente.</p>
              )}
            </div>
          </div>
        );
      
      // MAPPINGS EXISTANTS
      case 'UserProfileForm': 
        return <UserProfileForm db={db} userId={userId} setError={setError} setMessage={setMessage} />;
      case 'userRegistration': 
        return <UserRegistrationForm db={db} auth={getAuth()} userRole={userRole} setMessage={setMessage} setError={setError} schoolId={effectiveSchoolId} allSchoolsList={allSchoolsList} schoolName={schoolName} />;
      case 'userList': 
        return <UserTable db={db} userRole={userRole} schoolId={effectiveSchoolId} setMessage={setMessage} setError={setError} />;
      case 'userRoleManagement': 
        return <UserRoleManagement db={db} auth={getAuth()} userRole={userRole} schoolId={effectiveSchoolId} setMessage={setMessage} setError={setError} userProfile={userProfile} selectedSchoolForSuperAdmin={selectedSchoolForSuperAdmin}/>;
      case 'elevesManagement': 
        return <ElevesManagement db={db} storage={storage} schoolId={effectiveSchoolId} userRole={userRole} setMessage={setMessage} setError={setError} appVersion={appVersion} />;
      case 'enseignantsManagement': 
        return <EnseignantsManagement db={db} schoolId={effectiveSchoolId} userRole={userRole} setMessage={setMessage} setError={setError} />;
      case 'studentExcelImport': 
        return <StudentExcelImport db={db} schoolId={effectiveSchoolId} userRole={userRole} setMessage={setMessage} setError={setError} />;
      case 'matieresManagement': 
        return <MatieresManagement db={db} schoolId={effectiveSchoolId} userRole={userRole} setMessage={setMessage} setError={setError} />;
      case 'classesFraisManagement': 
        return <ClassesFraisManagement db={db} schoolId={effectiveSchoolId} userRole={userRole} setMessage={setMessage} setError={setError} />;
      case 'emploiDuTempsManagement': 
        return <EmploiDuTempsManagement db={db} schoolId={effectiveSchoolId} userRole={userRole} setMessage={setMessage} setError={setError} />;
      case 'notesManagement': 
        return <NotesManagement db={db} schoolId={effectiveSchoolId} userRole={userRole} setMessage={setMessage} setError={setError} currentUserId={userId} />;
      case 'bulletinManagement': 
        return <BulletinManagement db={db} schoolId={effectiveSchoolId} userRole={userRole} setMessage={setMessage} setError={setError} />;
      case 'coursManagement': 
        return <CoursManagement db={db} schoolId={effectiveSchoolId} userRole={userRole} userId={userProfile.uid} setMessage={setMessage} setError={setError} />;
      case 'paiementsManagement': 
        return <PaiementsManagement db={db} schoolId={effectiveSchoolId} userRole={userRole} setMessage={setMessage} setError={setError} />;
      case 'depensesManagement': 
        return <DepensesManagement db={db} schoolId={effectiveSchoolId} userRole={userRole} setMessage={setMessage} setError={setError} />;
      case 'bankingManagement': 
        return <BankingManagement db={db} schoolId={effectiveSchoolId} userRole={userRole} setMessage={setMessage} setError={setError} />;
      case 'financialStatementsManagement': 
        return <FinancialStatementsManagement db={db} schoolId={effectiveSchoolId} userRole={userRole} setMessage={setMessage} setError={setError} />;
      case 'annoncesManagement': 
        return <AnnoncesManagement db={db} schoolId={effectiveSchoolId} userRole={userRole} />;
      case 'subscriptionManagement': 
        return <SubscriptionManagement db={db} schoolId={effectiveSchoolId} userRole={userRole} setMessage={setMessage} setError={setError} />;
      case 'myPaiements': 
        return <MyPaiements db={db} schoolId={effectiveSchoolId} userRole={userRole} setMessage={setMessage} setError={setError} currentUserId={userId} />;
      case 'myNotes': 
        return <MyNotes db={db} schoolId={effectiveSchoolId} userRole={userRole} setMessage={setMessage} setError={setError} currentUserId={userId} />;
      case 'myBulletins': 
        return <MyBulletins db={db} schoolId={effectiveSchoolId} userRole={userRole} currentUserId={userProfile.uid} setMessage={setMessage} setError={setError} />;
      case 'aiChatAssistant': 
        return <AIChatAssistant db={db} auth={getAuth()} schoolId={effectiveSchoolId} userRole={userRole} setMessage={setMessage} setError={setError} currentUserId={userId} />;
      case 'schoolInfoManagement': 
        return <SchoolInfoManagement db={db} auth={getAuth()} storage={storage} schoolId={effectiveSchoolId} userRole={userRole} setMessage={setMessage} setError={setError} />;
      case 'superAdminSchoolManagement': 
        return <SuperAdminSchoolManagement db={db} auth={getAuth()} allSchoolsList={allSchoolsList} schoolId={effectiveSchoolId} userRole={userRole} setMessage={setMessage} setError={setError} />;
      case 'RetardsAbsencesManagement': 
        return <RetardsAbsencesManagement db={db} auth={getAuth()} schoolId={effectiveSchoolId} userRole={userRole} userUid={userProfile.uid} setMessage={setMessage} setError={setError} />;
      case 'teacherAttendanceInput': 
        return <TeacherAttendanceInput db={db} auth={getAuth()} schoolId={effectiveSchoolId} userRole={userRole} setMessage={setMessage} setError={setError} />;
      case 'MyRetardsAbsences': 
        return <MyRetardsAbsences db={db} schoolId={effectiveSchoolId} currentUserId={userProfile.uid} userRole={userRole} setMessage={setMessage} setError={setError} />;
      case 'parentChildLinking': 
        return <ParentChildLinking db={db} schoolId={effectiveSchoolId} userRole={userRole} setMessage={setMessage} setError={setError} />;
      case 'myEmploiDuTemps': 
        return <MyEmploiDuTemps db={db} schoolId={effectiveSchoolId} currentUserId={userProfile.uid} userRole={userRole} setError={setError} />;
      
      // MAPPINGS V2
      case 'Rapport': 
        return <RapportCaisse db={db} schoolId={effectiveSchoolId} currentUserId={userProfile.uid} userRole={userRole} setError={setError} />;
      case 'ConsumablesManagement': 
        return <ConsumablesManagement db={db} schoolId={effectiveSchoolId} currentUserId={userProfile?.uid} userRole={userRole} setError={setError} setMessage={setMessage} isAuthReady={!isLoadingUser} />;
      case 'teacherAttendance': 
        return <TeacherAttendanceReport db={db} schoolId={schoolId} setMessage={setMessage} setError={setError} />;
      
      // NOUVEAU : PAIEMENT WAVE QR (Visible pour toutes les écoles)
      case 'waveQRPayment':
        return (
          <div className="p-4 md:p-6">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-2 flex items-center">
                <QrCode className="mr-3 text-purple-600" size={28} />
                Paiement Abonnement avec Wave
              </h2>
              <p className="text-gray-600">
                Scannez le QR code Wave avec votre téléphone pour payer votre abonnement mensuel.
              </p>
            </div>
            
            <div className="max-w-6xl mx-auto">
              {waveQrCodeUrl ? (
                <WaveQRPaymentSimple
                  db={db}
                  schoolId={effectiveSchoolId}
                  userRole={userRole}
                  amount={10000}
                  description="Abonnement mensuel Jangal App"
                  waveQrCodeUrl={waveQrCodeUrl}
                  onSuccess={(data) => {
                    setMessage(`Paiement confirmé ! Code: ${data.confirmationCode}`);
                    setTimeout(() => setSelectedSection('dashboard'), 3000);
                  }}
                  onError={(error) => {
                    setError(`Erreur paiement: ${error}`);
                  }}
                />
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center">
                  <QrCode className="mx-auto text-yellow-500 mb-4" size={64} />
                  <h3 className="text-xl font-bold text-yellow-800 mb-2">QR Code Wave non configuré</h3>
                  <p className="text-yellow-700 mb-4">
                    Le QR code de paiement Wave n'a pas encore été configuré par l'administrateur.
                  </p>
                  {userRole === 'superAdmin' ? (
                    <button
                      onClick={() => handleNavigation('waveQRConfig')}
                      className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700"
                    >
                      Configurer le QR code Wave
                    </button>
                  ) : (
                    <p className="text-yellow-600">
                      Veuillez contacter l'administrateur pour configurer le paiement Wave.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      
      // NOUVEAU : CONFIGURATION QR CODE (Super Admin seulement)
      case 'waveQRConfig':
        if (userRole !== 'superAdmin') {
          return (
            <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
              <h2 className="text-xl font-bold text-red-800 mb-4">Accès refusé</h2>
              <p className="text-red-700">
                Seul le Super Administrateur peut configurer le QR code Wave.
              </p>
            </div>
          );
        }
        
        return (
          <div className="p-4 md:p-6">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-2 flex items-center">
                <Settings className="mr-3 text-blue-600" size={28} />
                Configuration du QR Code Wave
              </h2>
              <p className="text-gray-600">
                Configurez le QR code Wave qui sera utilisé par toutes les écoles pour payer leur abonnement.
              </p>
            </div>
            
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
                <h3 className="text-lg font-semibold mb-4">📋 Instructions</h3>
                <div className="bg-blue-50 rounded-lg p-4 mb-6">
                  <ul className="space-y-2 text-blue-700">
                    <li>1. Téléchargez votre QR code Wave depuis votre compte marchand</li>
                    <li>2. Assurez-vous qu'il pointe vers votre numéro Wave professionnel</li>
                    <li>3. Le QR code sera visible par toutes les écoles de la plateforme</li>
                    <li>4. Ne configurez pas de montant dans le QR code (10,000 FCFA fixe)</li>
                  </ul>
                </div>
                
                <WaveQRUpload 
                  storage={storage}
                  db={db}
                  userProfile={userProfile}
                  onUploadComplete={handleQrUploadComplete}
                />
              </div>
              
              {waveQrCodeUrl && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-green-800 mb-4 flex items-center">
                    <QrCode className="mr-2" />
                    QR Code Actuellement Configuré
                  </h3>
                  <div className="flex flex-col md:flex-row items-center gap-6">
                    <div className="bg-white p-4 rounded-lg shadow">
                      <img 
                        src={waveQrCodeUrl} 
                        alt="QR Code Wave actuel"
                        className="w-48 h-48 object-contain"
                      />
                    </div>
                    <div className="flex-1">
                      <p className="text-gray-700 mb-2">
                        Ce QR code est actuellement utilisé par toutes les écoles.
                      </p>
                      <div className="space-y-2">
                        <div>
                          <span className="font-semibold">Statut :</span>
                          <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 rounded text-sm">Actif</span>
                        </div>
                        <div>
                          <span className="font-semibold">Montant :</span>
                          <span className="ml-2">10,000 FCFA (fixe)</span>
                        </div>
                        <div>
                          <span className="font-semibold">Destinataire :</span>
                          <span className="ml-2">Votre compte Wave marchand</span>
                        </div>
                      </div>
                      <button
                        onClick={() => window.open(waveQrCodeUrl, '_blank')}
                        className="mt-4 text-blue-600 hover:text-blue-800 flex items-center"
                      >
                        <span>Voir le QR code en grand</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      default: 
        return (
          <div className="p-6 bg-white rounded-lg shadow-md">
            <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
              <Home size={24} className="mr-2" /> Vue par défaut
            </h3>
            <p className="text-gray-700">Sélectionnez une section dans le menu.</p>
          </div>
        );
    }
  };

  if (isLoadingUser) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <Loader2 className="animate-spin text-blue-500 mr-3" size={48} />
        <p className="text-gray-700 text-lg">Chargement...</p>
      </div>
    );
  }

  if (!userProfile || !userProfile.profileCompleted) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-red-50 p-4">
        <div className="p-6 md:p-8 bg-white rounded-lg shadow-lg text-center max-w-md w-full">
          <XCircle size={48} className="text-red-600 mx-auto mb-4" />
          <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-4">Profil Incomplet</h2>
          <p className="text-gray-600 mb-6">Votre profil utilisateur doit être complété.</p>
          <button
            onClick={handleLogout}
            className="bg-red-600 text-white py-2.5 px-5 rounded-lg font-semibold w-full hover:bg-red-700"
          >
            Déconnexion
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-100 font-sans antialiased overflow-hidden">
      
      {/* OVERLAY MOBILE */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 z-20 bg-black bg-opacity-50 transition-opacity md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* SIDEBAR */}
      <aside 
        className={`
          fixed inset-y-0 left-0 z-30 w-64 bg-blue-700 text-white flex flex-col shadow-xl transform transition-transform duration-300 ease-in-out
          md:relative md:translate-x-0
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Header Sidebar */}
        <div className="flex items-center p-4 border-b border-blue-600 relative">
          {schoolLogoUrl ? (
            <img src={schoolLogoUrl} alt="Logo" className="h-10 w-10 object-contain rounded-full mr-3 bg-white" />
          ) : (
            <div className="h-10 w-10 bg-blue-500 rounded-full flex items-center justify-center text-lg font-bold mr-3 border-2 border-white">
              {schoolName ? schoolName.charAt(0).toUpperCase() : 'E'}
            </div>
          )}
          <div className="flex flex-col overflow-hidden w-full">
            <span className="text-lg font-semibold truncate">{schoolName || 'Mon École'}</span>
            <span className="text-xs font-normal text-blue-200 truncate">{userEmail}</span>
            <span className="text-[10px] uppercase tracking-wider text-blue-300 mt-1">{userRole}</span>
          </div>
          
          <button 
            onClick={() => setIsMobileMenuOpen(false)} 
            className="absolute top-2 right-2 p-1 text-blue-200 hover:text-white md:hidden"
          >
            <X size={20} />
          </button>
        </div>

        {/* Selecteur École (SuperAdmin) */}
        {userRole === 'superAdmin' && (
          <div className="px-3 py-3 bg-blue-800">
            <label htmlFor="schoolSelector" className="block text-xs font-medium text-blue-200 mb-1">
              Gérer l'école :
            </label>
            <select
              id="schoolSelector"
              value={selectedSchoolForSuperAdmin || "null"}
              onChange={handleSchoolSelectionChange}
              className="block w-full pl-2 pr-8 py-1.5 text-sm border-gray-300 focus:ring-blue-500 focus:border-blue-500 rounded text-gray-900 bg-white"
            >
              <option value="null">-- Sélectionner --</option>
              {allSchoolsList.map(school => (
                <option key={school.id} value={school.id}>{school.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* MENU SCROLLABLE */}
        <nav className="flex-grow space-y-1 overflow-y-auto p-2">
          <ul>
            {/* Menu principal */}
            <li>
              <button 
                onClick={() => handleNavigation('UserProfileForm')} 
                className={`w-full text-left py-2 px-3 rounded flex items-center ${selectedSection === 'UserProfileForm' ? 'bg-blue-800' : 'hover:bg-blue-600'}`}
              >
                <UserCog size={18} className="mr-3" /> Mon Profil
              </button>
            </li>
            <li>
              <button 
                onClick={() => handleNavigation('dashboard')} 
                className={`w-full text-left py-2 px-3 rounded flex items-center ${selectedSection === 'dashboard' ? 'bg-blue-800' : 'hover:bg-blue-600'}`}
              >
                <LayoutDashboard size={18} className="mr-3" /> Tableau de Bord
              </button>
            </li>

            {/* SUPER ADMIN SPECIFIQUE */}
            {userRole === 'superAdmin' && (
              <>
                <li className="mt-4 pt-2 border-t border-blue-600/50">
                  <span className="text-xs font-bold text-blue-300 uppercase px-3 mb-1 block">Super Admin</span>
                </li>
                <li>
                  <button 
                    onClick={() => handleNavigation('superAdminSchoolManagement')} 
                    className={`w-full text-left py-2 px-3 rounded flex items-center ${selectedSection === 'superAdminSchoolManagement' ? 'bg-blue-800' : 'hover:bg-blue-600'}`}
                  >
                    <School size={18} className="mr-3" /> Gestion Écoles
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => handleNavigation('waveQRConfig')} 
                    className={`w-full text-left py-2 px-3 rounded flex items-center ${selectedSection === 'waveQRConfig' ? 'bg-blue-800' : 'hover:bg-blue-600'}`}
                  >
                    <QrCode size={18} className="mr-3" /> Configurer QR Wave
                  </button>
                </li>
              </>
            )}

            {/* ADMINISTRATION */}
            {(userRole === 'superAdmin' || userRole === 'admin' || userRole === 'secretaireGeneral') && (
              <>
                <li className="mt-4 pt-2 border-t border-blue-600/50">
                  <span className="text-xs font-bold text-blue-300 uppercase px-3 mb-1 block">Administration</span>
                </li>
                <li>
                  <button 
                    onClick={() => handleNavigation('userRegistration')} 
                    className={`w-full text-left py-2 px-3 rounded flex items-center ${selectedSection === 'userRegistration' ? 'bg-blue-800' : 'hover:bg-blue-600'}`}
                  >
                    <UserPlus size={18} className="mr-3" /> Création utilisateur
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => handleNavigation('userRoleManagement')} 
                    className={`w-full text-left py-2 px-3 rounded flex items-center ${selectedSection === 'userRoleManagement' ? 'bg-blue-800' : 'hover:bg-blue-600'}`}
                  >
                    <Users size={18} className="mr-3" /> Rôles & Accès
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => handleNavigation('parentChildLinking')} 
                    className={`w-full text-left py-2 px-3 rounded flex items-center ${selectedSection === 'parentChildLinking' ? 'bg-blue-800' : 'hover:bg-blue-600'}`}
                  >
                    <Link size={18} className="mr-3" /> Liaison Parents
                  </button>
                </li>
              </>
            )}

            {/* SCOLARITÉ */}
            {((['admin', 'directeurPedagogique', 'secretaireGeneral'].includes(userRole) && schoolId) || (userRole === 'superAdmin' && selectedSchoolForSuperAdmin)) && (
              <>
                <li className="mt-4 pt-2 border-t border-blue-600/50">
                  <span className="text-xs font-bold text-blue-300 uppercase px-3 mb-1 block">Scolarité</span>
                </li>
                <li>
                  <button 
                    onClick={() => handleNavigation('elevesManagement')} 
                    className={`w-full text-left py-2 px-3 rounded flex items-center ${selectedSection === 'elevesManagement' ? 'bg-blue-800' : 'hover:bg-blue-600'}`}
                  >
                    <GraduationCap size={18} className="mr-3" /> Élèves
                  </button>
                </li>
                
                {/* BOUTON IMPORT EXCEL */}
                <li>
                  <button 
                    onClick={() => handleNavigation('studentExcelImport')} 
                    className={`w-full text-left py-2 px-3 rounded flex items-center ${selectedSection === 'studentExcelImport' ? 'bg-blue-800' : 'hover:bg-blue-600'}`}
                  >
                    <UserRoundCheck size={18} className="mr-3" /> Import Élèves (Excel)
                  </button>
                </li>

                <li>
                  <button 
                    onClick={() => handleNavigation('enseignantsManagement')} 
                    className={`w-full text-left py-2 px-3 rounded flex items-center ${selectedSection === 'enseignantsManagement' ? 'bg-blue-800' : 'hover:bg-blue-600'}`}
                  >
                    <BookUser size={18} className="mr-3" /> Enseignants
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => handleNavigation('RetardsAbsencesManagement')} 
                    className={`w-full text-left py-2 px-3 rounded flex items-center ${selectedSection === 'RetardsAbsencesManagement' ? 'bg-blue-800' : 'hover:bg-blue-600'}`}
                  >
                    <ClipboardList size={18} className="mr-3" /> Retards/Absences
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => handleNavigation('matieresManagement')} 
                    className={`w-full text-left py-2 px-3 rounded flex items-center ${selectedSection === 'matieresManagement' ? 'bg-blue-800' : 'hover:bg-blue-600'}`}
                  >
                    <BookOpenText size={18} className="mr-3" /> Matières
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => handleNavigation('classesFraisManagement')} 
                    className={`w-full text-left py-2 px-3 rounded flex items-center ${selectedSection === 'classesFraisManagement' ? 'bg-blue-800' : 'hover:bg-blue-600'}`}
                  >
                    <School size={18} className="mr-3" /> Classes
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => handleNavigation('emploiDuTempsManagement')} 
                    className={`w-full text-left py-2 px-3 rounded flex items-center ${selectedSection === 'emploiDuTempsManagement' ? 'bg-blue-800' : 'hover:bg-blue-600'}`}
                  >
                    <CalendarDays size={18} className="mr-3" /> Emploi du Temps
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => handleNavigation('notesManagement')} 
                    className={`w-full text-left py-2 px-3 rounded flex items-center ${selectedSection === 'notesManagement' ? 'bg-blue-800' : 'hover:bg-blue-600'}`}
                  >
                    <FileText size={18} className="mr-3" /> Notes
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => handleNavigation('bulletinManagement')} 
                    className={`w-full text-left py-2 px-3 rounded flex items-center ${selectedSection === 'bulletinManagement' ? 'bg-blue-800' : 'hover:bg-blue-600'}`}
                  >
                    <ClipboardCheck size={18} className="mr-3" /> Bulletins
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => handleNavigation('annoncesManagement')} 
                    className={`w-full text-left py-2 px-3 rounded flex items-center ${selectedSection === 'annoncesManagement' ? 'bg-blue-800' : 'hover:bg-blue-600'}`}
                  >
                    <Megaphone size={18} className="mr-3" /> Annonces
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => handleNavigation('coursManagement')} 
                    className={`w-full text-left py-2 px-3 rounded flex items-center ${selectedSection === 'coursManagement' ? 'bg-blue-800' : 'hover:bg-blue-600'}`}
                  >
                    <BookOpenText size={18} className="mr-3" /> Fiches de cours
                  </button>
                </li>
                
                {hasAccess('ConsumablesManagement') && (
                  <li>
                    <button 
                      onClick={() => handleNavigation('ConsumablesManagement')} 
                      className={`w-full text-left py-2 px-3 rounded flex items-center ${selectedSection === 'ConsumablesManagement' ? 'bg-blue-800' : 'hover:bg-blue-600'}`}
                    >
                      <LayoutDashboard size={18} className="mr-3" /> Stocks (V2)
                    </button>
                  </li>
                )}
                
                <li>
                  <button 
                    onClick={() => handleNavigation('schoolInfoManagement')} 
                    className={`w-full text-left py-2 px-3 rounded flex items-center ${selectedSection === 'schoolInfoManagement' ? 'bg-blue-800' : 'hover:bg-blue-600'}`}
                  >
                    <Settings size={18} className="mr-3" /> Paramètres École
                  </button>
                </li>
              </>
            )}

            {/* FINANCE */}
            {((['financier', 'admin'].includes(userRole) && schoolId) || (userRole === 'superAdmin' && selectedSchoolForSuperAdmin)) && (
              <>
                <li className="mt-4 pt-2 border-t border-blue-600/50">
                  <span className="text-xs font-bold text-blue-300 uppercase px-3 mb-1 block">Finances</span>
                </li>
                <li>
                  <button 
                    onClick={() => handleNavigation('paiementsManagement')} 
                    className={`w-full text-left py-2 px-3 rounded flex items-center ${selectedSection === 'paiementsManagement' ? 'bg-blue-800' : 'hover:bg-blue-600'}`}
                  >
                    <Wallet size={18} className="mr-3" /> Paiements
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => handleNavigation('depensesManagement')} 
                    className={`w-full text-left py-2 px-3 rounded flex items-center ${selectedSection === 'depensesManagement' ? 'bg-blue-800' : 'hover:bg-blue-600'}`}
                  >
                    <CreditCard size={18} className="mr-3" /> Dépenses
                  </button>
                </li>
                
                {/* PAIEMENT WAVE QR (Visible pour toutes les écoles) */}
                <li>
                  <button 
                    onClick={() => handleNavigation('waveQRPayment')} 
                    className={`w-full text-left py-2 px-3 rounded flex items-center ${selectedSection === 'waveQRPayment' ? 'bg-blue-800' : 'hover:bg-blue-600'}`}
                  >
                    <QrCode size={18} className="mr-3" /> Paiement Wave QR
                  </button>
                </li>
                
                {/* Modules V3 - Finances Avancées */}
                {hasAccess('bankingManagement') && (
                  <li>
                    <button 
                      onClick={() => handleNavigation('bankingManagement')} 
                      className={`w-full text-left py-2 px-3 rounded flex items-center ${selectedSection === 'bankingManagement' ? 'bg-blue-800' : 'hover:bg-blue-600'}`}
                    >
                      <CreditCard size={18} className="mr-3" /> Banques (V3)
                    </button>
                  </li>
                )}
                {hasAccess('financialStatementsManagement') && (
                  <li>
                    <button 
                      onClick={() => handleNavigation('financialStatementsManagement')} 
                      className={`w-full text-left py-2 px-3 rounded flex items-center ${selectedSection === 'financialStatementsManagement' ? 'bg-blue-800' : 'hover:bg-blue-600'}`}
                    >
                      <DollarSign size={18} className="mr-3" /> États Financiers (V3)
                    </button>
                  </li>
                )}
              </>
            )}

            {/* ESPACE ENSEIGNANT */}
            {(userRole === 'enseignant' && schoolId) || (userRole === 'superAdmin' && selectedSchoolForSuperAdmin) ? (
              <>
                <li className="mt-4 pt-2 border-t border-blue-600/50">
                  <span className="text-xs font-bold text-blue-300 uppercase px-3 mb-1 block">Espace Enseignant</span>
                </li>
                <li>
                  <button 
                    onClick={() => handleNavigation('emploiDuTempsManagement')} 
                    className={`w-full text-left py-2 px-3 rounded flex items-center ${selectedSection === 'emploiDuTempsManagement' ? 'bg-blue-800' : 'hover:bg-blue-600'}`}
                  >
                    <CalendarDays size={18} className="mr-3" /> Mon Emploi du Temps
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => handleNavigation('notesManagement')} 
                    className={`w-full text-left py-2 px-3 rounded flex items-center ${selectedSection === 'notesManagement' ? 'bg-blue-800' : 'hover:bg-blue-600'}`}
                  >
                    <FileText size={18} className="mr-3" /> Gérer Notes
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => handleNavigation('RetardsAbsencesManagement')} 
                    className={`w-full text-left py-2 px-3 rounded flex items-center ${selectedSection === 'RetardsAbsencesManagement' ? 'bg-blue-800' : 'hover:bg-blue-600'}`}
                  >
                    <ClipboardList size={18} className="mr-3" /> Appel / Présence
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => handleNavigation('coursManagement')} 
                    className={`w-full text-left py-2 px-3 rounded flex items-center ${selectedSection === 'coursManagement' ? 'bg-blue-800' : 'hover:bg-blue-600'}`}
                  >
                    <BookOpenText size={18} className="mr-3" /> Mes Fiches
                  </button>
                </li>
                {hasAccess('teacherAttendance') && (
                  <li>
                    <button 
                      onClick={() => handleNavigation('teacherAttendance')} 
                      className={`w-full text-left py-2 px-3 rounded flex items-center ${selectedSection === 'teacherAttendance' ? 'bg-blue-800' : 'hover:bg-blue-600'}`}
                    >
                      <ClipboardList size={18} className="mr-3" /> Présence Enseignants
                    </button>
                  </li>
                )}
              </>
            ) : null}

            {/* ESPACE ÉTUDIANT */}
            {(userRole === 'etudiant' && schoolId) || (userRole === 'superAdmin' && selectedSchoolForSuperAdmin) ? (
              <>
                <li className="mt-4 pt-2 border-t border-blue-600/50">
                  <span className="text-xs font-bold text-blue-300 uppercase px-3 mb-1 block">Espace Étudiant</span>
                </li>
                <li>
                  <button 
                    onClick={() => handleNavigation('myEmploiDuTemps')} 
                    className={`w-full text-left py-2 px-3 rounded flex items-center ${selectedSection === 'myEmploiDuTemps' ? 'bg-blue-800' : 'hover:bg-blue-600'}`}
                  >
                    <CalendarDays size={18} className="mr-3" /> Mon Emploi du Temps
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => handleNavigation('myNotes')} 
                    className={`w-full text-left py-2 px-3 rounded flex items-center ${selectedSection === 'myNotes' ? 'bg-blue-800' : 'hover:bg-blue-600'}`}
                  >
                    <FileText size={18} className="mr-3" /> Mes Notes
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => handleNavigation('MyRetardsAbsences')} 
                    className={`w-full text-left py-2 px-3 rounded flex items-center ${selectedSection === 'MyRetardsAbsences' ? 'bg-blue-800' : 'hover:bg-blue-600'}`}
                  >
                    <ClipboardList size={18} className="mr-3" /> Mes Absences
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => handleNavigation('myPaiements')} 
                    className={`w-full text-left py-2 px-3 rounded flex items-center ${selectedSection === 'myPaiements' ? 'bg-blue-800' : 'hover:bg-blue-600'}`}
                  >
                    <Wallet size={18} className="mr-3" /> Mes Paiements
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => handleNavigation('myBulletins')} 
                    className={`w-full text-left py-2 px-3 rounded flex items-center ${selectedSection === 'myBulletins' ? 'bg-blue-800' : 'hover:bg-blue-600'}`}
                  >
                    <ClipboardCheck size={18} className="mr-3" /> Mes Bulletins
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => handleNavigation('aiChatAssistant')} 
                    className={`w-full text-left py-2 px-3 rounded flex items-center ${selectedSection === 'aiChatAssistant' ? 'bg-blue-800' : 'hover:bg-blue-600'}`}
                  >
                    <List size={18} className="mr-3" /> Assistant IA
                  </button>
                </li>
              </>
            ) : null}

            {/* ESPACE PARENT */}
            {(userRole === 'parent' && schoolId) ? (
              <>
                <li className="mt-4 pt-2 border-t border-blue-600/50">
                  <span className="text-xs font-bold text-blue-300 uppercase px-3 mb-1 block">Espace Parent</span>
                </li>
                <li>
                  <button 
                    onClick={() => handleNavigation('myEmploiDuTemps')} 
                    className={`w-full text-left py-2 px-3 rounded flex items-center ${selectedSection === 'myEmploiDuTemps' ? 'bg-blue-800' : 'hover:bg-blue-600'}`}
                  >
                    <CalendarDays size={18} className="mr-3" /> Emploi du Temps Enfants
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => handleNavigation('myNotes')} 
                    className={`w-full text-left py-2 px-3 rounded flex items-center ${selectedSection === 'myNotes' ? 'bg-blue-800' : 'hover:bg-blue-600'}`}
                  >
                    <FileText size={18} className="mr-3" /> Notes Enfants
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => handleNavigation('myBulletins')} 
                    className={`w-full text-left py-2 px-3 rounded flex items-center ${selectedSection === 'myBulletins' ? 'bg-blue-800' : 'hover:bg-blue-600'}`}
                  >
                    <ClipboardCheck size={18} className="mr-3" /> Bulletins Enfants
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => handleNavigation('myPaiements')} 
                    className={`w-full text-left py-2 px-3 rounded flex items-center ${selectedSection === 'myPaiements' ? 'bg-blue-800' : 'hover:bg-blue-600'}`}
                  >
                    <Wallet size={18} className="mr-3" /> Paiements Enfants
                  </button>
                </li>
              </>
            ) : null}
          </ul>
        </nav>
      </aside>

      {/* ZONE DE CONTENU PRINCIPALE */}
      <div className="flex-1 flex flex-col h-screen overflow-hidden">
        
        {/* EN-TÊTE */}
        <header className="bg-white shadow-sm z-10 p-4 flex items-center justify-between shrink-0">
          <div className="flex items-center">
            {/* Bouton Hamburger */}
            <button 
              onClick={() => setIsMobileMenuOpen(true)}
              className="mr-4 text-gray-600 hover:text-blue-600 focus:outline-none md:hidden"
            >
              <Menu size={28} />
            </button>
            
            <h1 className="text-xl md:text-2xl font-bold text-gray-800 truncate max-w-[200px] sm:max-w-md">
              {selectedSection === 'dashboard' ? (schoolName || 'Tableau de Bord') : 
               selectedSection === 'UserProfileForm' ? 'Mon Profil' : 
               selectedSection === 'userRegistration' ? 'Création Utilisateur' :
               selectedSection === 'userList' ? 'Liste Utilisateurs' :
               selectedSection === 'userRoleManagement' ? 'Gestion Rôles' :
               selectedSection === 'waveQRPayment' ? 'Paiement Wave QR' :
               selectedSection === 'waveQRConfig' ? 'Configuration QR Wave' :
               selectedSection === 'elevesManagement' ? 'Gestion Élèves' :
               selectedSection === 'enseignantsManagement' ? 'Gestion Enseignants' :
               selectedSection === 'studentExcelImport' ? 'Import Élèves (Excel)' :
               selectedSection === 'matieresManagement' ? 'Gestion Matières' :
               selectedSection === 'classesFraisManagement' ? 'Gestion Classes' :
               selectedSection === 'emploiDuTempsManagement' ? 'Emploi du Temps' :
               selectedSection === 'notesManagement' ? 'Gestion Notes' :
               selectedSection === 'bulletinManagement' ? 'Gestion Bulletins' :
               selectedSection === 'coursManagement' ? 'Fiches de Cours' :
               selectedSection === 'paiementsManagement' ? 'Gestion Paiements' :
               selectedSection === 'depensesManagement' ? 'Gestion Dépenses' :
               selectedSection === 'bankingManagement' ? 'Gestion Bancaire' :
               selectedSection === 'financialStatementsManagement' ? 'États Financiers' :
               selectedSection === 'annoncesManagement' ? 'Gestion Annonces' :
               selectedSection === 'schoolInfoManagement' ? 'Paramètres École' :
               selectedSection === 'superAdminSchoolManagement' ? 'Gestion Écoles' :
               selectedSection === 'RetardsAbsencesManagement' ? 'Retards & Absences' :
               selectedSection === 'teacherAttendanceInput' ? 'Saisie Présence' :
               selectedSection === 'MyRetardsAbsences' ? 'Mes Absences' :
               selectedSection === 'parentChildLinking' ? 'Liaison Parent-Enfant' :
               selectedSection === 'myEmploiDuTemps' ? 'Mon Emploi du Temps' :
               selectedSection === 'myPaiements' ? 'Mes Paiements' :
               selectedSection === 'myNotes' ? 'Mes Notes' :
               selectedSection === 'myBulletins' ? 'Mes Bulletins' :
               selectedSection === 'aiChatAssistant' ? 'Assistant IA' :
               selectedSection === 'Rapport' ? 'Rapport de Caisse' :
               selectedSection === 'ConsumablesManagement' ? 'Gestion Stocks' :
               selectedSection === 'teacherAttendance' ? 'Présence Enseignants' :
               selectedSection === 'subscriptionManagement' ? 'Gestion Abonnements' :
               'Administration'}
            </h1>
          </div>

          <button
            onClick={handleLogout}
            className="text-red-600 hover:bg-red-50 p-2 rounded-full md:rounded-lg md:px-4 md:py-2 md:bg-red-600 md:text-white md:hover:bg-red-700 flex items-center transition duration-200"
            title="Déconnexion"
          >
            <LogOut size={20} className="md:mr-2" />
            <span className="hidden md:inline font-semibold">Déconnexion</span>
          </button>
        </header>

        {/* CONTENU */}
        <main className="flex-1 overflow-y-auto p-4 md:p-8 bg-gray-100">
          <div className="max-w-7xl mx-auto">
            {renderContent()}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;